import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { desktop, Spacing } from '@hg/stylizator/utils'
import parse from 'html-react-parser'
import * as React from 'react'
import styled, { css } from 'styled-components'

import ShareButton from './ShareButton'
import ClientOnly from '../ClientOnly'

import PostMeta from 'app/components/blog/PostMeta'
import Tag from 'app/components/Tag'
import useHasMounted from 'app/hooks/use-has-mounted'
import { Post as IPost } from 'app/service/api/theme/post'

type Variant = 'large' | 'medium' | 'small' | 'xsmall'

interface IProps {
  variant: Variant
  post: IPost
  withShare?: boolean
  titleHeading?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const titleTypeMap = {
  large: 'heading_large',
  medium: 'heading_small',
  small: 'heading_small',
  xsmall: 'heading_xsmall',
} as const

const titleTypeMapMobile = {
  large: 'compact_heading_large',
  medium: 'compact_heading_xsmall',
  small: 'heading_small',
  xsmall: 'compact_body',
} as const

const Post: React.FC<IProps> = ({ post, variant, withShare, titleHeading: TitleHeading = 'h3' }) => {
  const { isDesktop } = useSizes()
  const hasMounted = useHasMounted()

  const thumbnailImage = parse(post.thumbnail, { trim: true }) as React.ReactElement
  const { height, width, ...thumbnailProps } = thumbnailImage?.props ?? {}

  const showShare = withShare && isDesktop && variant === 'large'

  const titleType = React.useMemo(() => {
    const titleMap = isDesktop ? titleTypeMap : titleTypeMapMobile
    return titleMap[variant]
  }, [isDesktop, hasMounted, variant])

  return (
    <Container variant={variant}>
      <ThumbnailWrapper variant={variant} href={post.permalink}>
        <Thumbnail {...thumbnailProps} width="100%" height="auto" />
      </ThumbnailWrapper>

      <div style={{ flex: 1 }}>
        <Pills>
          {post.categories.map((category) => (
            <Tag key={category.url} href={category.url}>
              {category.name}
            </Tag>
          ))}
        </Pills>

        <Spacing top>
          <a href={post.permalink}>
            <ClientOnly>
              <Text type={titleType} fontWeight={700}>
                <TitleHeading>{parse(post.title)}</TitleHeading>
              </Text>
            </ClientOnly>
          </a>
        </Spacing>

        {!!post.excerpt && (
          <Spacing top={variant === 'large' ? 2 : 1} className="excerpt">
            <Text type="heading_xsmall" useCompact fontWeight={400}>
              <Excerpt>{parse(post.excerpt.replace(' [&hellip;]', '...'))}</Excerpt>
            </Text>
          </Spacing>
        )}

        <PostFooter variant={variant}>
          <PostMeta
            author={variant === 'large' ? post.author : undefined}
            createdAt={post.created_at}
            updatedAt={post.updated_at}
            readingTime={post.reading_time_min}
            textSmall={variant !== 'large'}
          />
          <ClientOnly>{showShare && <ShareButton title={post.title} permalink={post.permalink} />}</ClientOnly>
        </PostFooter>
      </div>
    </Container>
  )
}

const Container = styled.div<{ variant: Variant }>`
  display: flex;
  gap: ${(props) => (props.variant === 'xsmall' ? '16px' : '24px')};
  flex-direction: ${(props) => (props.variant === 'xsmall' || props.variant === 'small' ? 'row' : 'column')};

  .excerpt {
    display: ${(props) => (props.variant === 'large' ? 'block' : 'none')};
  }

  @media ${desktop} {
    .excerpt {
      display: ${(props) => (props.variant === 'xsmall' ? 'none' : 'block')};
    }
  }
`

const ThumbnailWrapper = styled.a<{ variant: Variant }>`
  ${(props) =>
    props.variant === 'xsmall' &&
    css`
      max-width: 130px;
    `}

  ${(props) =>
    props.variant === 'small' &&
    css`
      max-width: 240px;
    `}
`

const Thumbnail = styled.img`
  width: 100%;
  aspect-ratio: 3 / 2;
  border-radius: 8px;
`

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const PostFooter = styled.div<{ variant: Variant }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;

  ${(props) =>
    props.variant === 'large' &&
    css`
      margin-top: 24px;
    `}

  ${(props) =>
    props.variant === 'medium' &&
    desktop &&
    css`
      margin-top: 16px;
    `}
`

const Excerpt = styled.p`
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default Post
